export const CONSULTANT_STORAGE_VERSION = 1;
export const CONSULTANT_STORAGE_PREFIX = "siger_consultant:v1:user:";

const getStorage = (storage) => {
  if (storage) return storage;
  if (typeof window === "undefined") return null;
  return window.localStorage;
};

const normalizeUserId = (userId) => {
  const value = String(userId ?? "").trim();
  return value && value !== "undefined" && value !== "null" ? value : null;
};

export const getConsultantStorageKey = (userId) => {
  const ownerId = normalizeUserId(userId);
  return ownerId ? `${CONSULTANT_STORAGE_PREFIX}${ownerId}` : null;
};

const sanitizeMessages = (messages) => (Array.isArray(messages) ? messages : [])
  .filter((message) => message && ["user", "assistant"].includes(message.role))
  .map((message) => ({
    id: String(message.id || "").slice(0, 128),
    role: message.role,
    content: String(message.content || "").slice(0, 8_000),
    createdAt: String(message.createdAt || "").slice(0, 40),
    suggestedActions: (Array.isArray(message.suggestedActions) ? message.suggestedActions : [])
      .filter((action) => typeof action === "string" && action.trim())
      .slice(0, 10),
    reportCharts: (Array.isArray(message.reportCharts) ? message.reportCharts : [])
      .filter((chart) => chart && typeof chart === "object" && !Array.isArray(chart))
      .slice(0, 6),
    clarificationQuestion: typeof message.clarificationQuestion === "string"
      ? message.clarificationQuestion.slice(0, 1_000)
      : null,
  }))
  .filter((message) => message.id && message.content)
  .slice(-60);

const isPlainObject = (value) => (
  value !== null && typeof value === "object" && !Array.isArray(value)
);

export const createEmptyConsultantSession = (userId, { open = false } = {}) => ({
  ownerId: normalizeUserId(userId),
  open: Boolean(open),
  messages: [],
  conversationId: null,
  conversationState: {},
  conversationStateToken: null,
  loading: false,
  error: null,
});

export const loadConsultantSession = (userId, storage) => {
  const ownerId = normalizeUserId(userId);
  const key = getConsultantStorageKey(ownerId);
  const target = getStorage(storage);
  if (!key || !target) return createEmptyConsultantSession(ownerId);

  try {
    const parsed = JSON.parse(target.getItem(key) || "null");
    if (
      !parsed ||
      parsed.version !== CONSULTANT_STORAGE_VERSION ||
      String(parsed.ownerId) !== ownerId
    ) {
      if (parsed) target.removeItem(key);
      return createEmptyConsultantSession(ownerId);
    }

    return {
      ...createEmptyConsultantSession(ownerId),
      open: Boolean(parsed.open),
      messages: sanitizeMessages(parsed.messages),
      conversationId: typeof parsed.conversationId === "string" ? parsed.conversationId : null,
      conversationState: isPlainObject(parsed.conversationState) ? parsed.conversationState : {},
      conversationStateToken: typeof parsed.conversationStateToken === "string"
        ? parsed.conversationStateToken
        : null,
    };
  } catch (error) {
    target.removeItem(key);
    return createEmptyConsultantSession(ownerId);
  }
};

export const saveConsultantSession = (userId, session, storage) => {
  const ownerId = normalizeUserId(userId);
  const key = getConsultantStorageKey(ownerId);
  const target = getStorage(storage);
  if (!key || !target || String(session?.ownerId) !== ownerId) return;

  const payload = {
    version: CONSULTANT_STORAGE_VERSION,
    ownerId,
    open: Boolean(session.open),
    messages: sanitizeMessages(session.messages),
    conversationId: typeof session.conversationId === "string" ? session.conversationId : null,
    conversationState: isPlainObject(session.conversationState) ? session.conversationState : {},
    conversationStateToken: typeof session.conversationStateToken === "string"
      ? session.conversationStateToken
      : null,
  };

  try {
    target.setItem(key, JSON.stringify(payload));
  } catch (error) {
    // Storage can be unavailable or full. The in-memory conversation remains usable.
  }
};

export const clearConsultantSession = (userId, storage) => {
  const key = getConsultantStorageKey(userId);
  const target = getStorage(storage);
  if (key && target) target.removeItem(key);
};

export const clearAllConsultantStorage = (storage) => {
  const target = getStorage(storage);
  if (!target) return;
  const keys = [];
  for (let index = 0; index < target.length; index += 1) {
    const key = target.key(index);
    if (key?.startsWith(CONSULTANT_STORAGE_PREFIX)) keys.push(key);
  }
  keys.forEach((key) => target.removeItem(key));
};

